import { z } from 'zod'
import { basisPointsSchema, optionalText, text } from './common.js'
import { paymentFeeRatesSchema } from './payment-method.js'

/**
 * Business configuration, editable in the UI and effective immediately.
 *
 * Values that must not change retroactively (the VAT rate, the business
 * identity printed on a document) are SNAPSHOTTED onto jobcards and documents
 * at creation. Editing them here changes what future documents say, never what
 * an already-issued one said.
 */
export const settingsSchema = z.object({
  // Business identity
  businessName: text(120),
  businessAddress: z.string().trim().max(500).default(''),
  businessPhone: optionalText(32),
  businessEmail: optionalText(120),
  /** UAE Tax Registration Number, or the local equivalent. */
  taxNumber: optionalText(40),
  website: optionalText(120),
  /** Path relative to DATA_DIR, e.g. "uploads/logo.png". Null when unset. */
  logoPath: z.string().nullable().default(null),

  // Money
  currencyCode: text(8).default('AED'),
  currencySymbol: text(8).default('AED'),
  /** Display only. Storage is always hundredths regardless of this value. */
  currencyDecimals: z.number().int().min(0).max(2).default(2),

  // VAT
  vatEnabledByDefault: z.boolean().default(true),
  vatRateBp: basisPointsSchema.default(500),
  /** Printed on documents: "VAT", "TVA", "GST". */
  vatLabel: text(20).default('VAT'),

  // Numbering
  jobcardPrefix: text(10).default('JC'),
  quotationPrefix: text(10).default('QUO'),
  invoicePrefix: text(10).default('INV'),
  /** Embed the year: JC-2026-0001 rather than JC-0001. */
  numberIncludesYear: z.boolean().default(true),
  numberPadding: z.number().int().min(1).max(10).default(4),

  // Documents
  quotationValidityDays: z.number().int().min(0).max(365).default(14),
  /** 0 means due on receipt. */
  invoicePaymentTermsDays: z.number().int().min(0).max(365).default(0),
  documentFooterTerms: z.string().trim().max(2000).default(''),
  /** Printed in the invoice's bank block. Blank lines are omitted. */
  bankName: z.string().trim().max(120).default(''),
  bankAccountTitle: z.string().trim().max(120).default(''),
  bankAccountNumber: z.string().trim().max(60).default(''),
  bankIban: z.string().trim().max(60).default(''),
  bankBranchCode: z.string().trim().max(60).default(''),
  bankDetails: z.string().trim().max(1000).default(''),

  // Localisation
  dateFormat: z.enum(['dd/MM/yyyy', 'yyyy-MM-dd', 'MM/dd/yyyy']).default('dd/MM/yyyy'),
  timezone: text(60).default('Asia/Dubai'),
  /**
   * Put on any phone number typed without one, so a local number reaches
   * WhatsApp and can be dialled from abroad. Blank switches it off, for a
   * workshop that would rather type numbers itself.
   */
  defaultCountryCode: z.string().trim().max(8).default('+971'),

  // Defaults offered in the editor
  defaultLabourRateMinor: z.number().int().min(0).default(0),

  /**
   * Absolute public base for customer links, e.g. https://garage.tailnet.ts.net.
   * The server cannot know its own tunnelled address, so it must be told. Blank
   * means links cannot be built and the staff panel says so.
   */
  publicBaseUrl: z.string().trim().max(200).default(''),

  /**
   * Excel sync: filenames (not full paths — the server can only see inside its
   * own data folder) read by the Accounting tab's "Sync from Excel" button,
   * one per expense category. Resolved against DATA_DIR/excel-imports. Blank
   * means that category has nothing configured yet.
   */
  excelSyncGeneralFilename: z.string().trim().max(200).default(''),
  excelSyncPartsFilename: z.string().trim().max(200).default(''),
  excelSyncPaintFilename: z.string().trim().max(200).default(''),

  /**
   * Late-arrival policy: how a worker's clocked-in time turns into a salary
   * deduction. Minutes late are measured from `latePolicyOpenTime`; nothing
   * is owed inside `latePolicyBufferMinutes`, but the moment that buffer is
   * exceeded the FULL lateness (not just the part past the buffer) is
   * charged at `latePolicyMultiplier` times the worker's per-minute rate,
   * itself derived from their monthly salary spread over
   * `latePolicyWorkingDaysPerMonth` days of `latePolicyShiftMinutes` each.
   */
  latePolicyEnabled: z.boolean().default(false),
  /** Shift start, "HH:mm". */
  latePolicyOpenTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm')
    .default('08:30'),
  latePolicyBufferMinutes: z.number().int().min(0).max(240).default(15),
  latePolicyMultiplier: z.number().min(1).max(10).default(2),
  latePolicyWorkingDaysPerMonth: z.number().int().min(1).max(31).default(26),
  latePolicyShiftMinutes: z.number().int().min(1).max(1440).default(480),

  /**
   * Overtime-pay policy: how a worker's departure time turns into overtime pay
   * the company owes them. Minutes over are measured from `overtimePolicyCloseTime`;
   * nothing is owed inside `overtimePolicyBufferMinutes`, but the moment that buffer
   * is exceeded the FULL time over close time (not just the part past the buffer) is
   * paid at `overtimePolicyMultiplier` times the worker's per-minute rate, itself
   * derived from their monthly salary spread over `latePolicyWorkingDaysPerMonth` days
   * of `latePolicyShiftMinutes` each. This REUSES the late-policy's working days and
   * shift duration to compute the per-minute rate, so edit those fields to change
   * the overtime rate.
   */
  overtimePolicyEnabled: z.boolean().default(false),
  /** Shift end, "HH:mm". */
  overtimePolicyCloseTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm')
    .default('17:30'),
  overtimePolicyBufferMinutes: z.number().int().min(0).max(240).default(15),
  overtimePolicyMultiplier: z.number().min(1).max(10).default(2),

  // Feature toggles. Only flags that gate code which actually exists.
  whatsappEnabled: z.boolean().default(true),
  /** The internal cost/revenue/margin strip in the jobcard editor. */
  showMargin: z.boolean().default(true),
  /** Customer appointments on the Calendar and Dashboard. */
  appointmentsEnabled: z.boolean().default(true),
  /**
   * 'simplified' = today's Finance/Accounting behaviour, untouched. 'advanced'
   * adds overhead costs, other income, staff cost and a full profit & loss.
   * Switching never deletes data.
   */
  accountingMode: z.enum(['simplified', 'advanced']).default('simplified'),
  /** Template for the WhatsApp prefilled message. See renderWhatsappMessage. */
  whatsappTemplate: z
    .string()
    .max(1000)
    .default(
      'Hello {{client_name}}, your {{document_type}} {{document_number}} for {{car}} is ready. Total: {{total}}. View your jobcard: {{link}} — {{business_name}}',
    ),

  // Payment methods
  paymentFeeRates: paymentFeeRatesSchema,
})
export type Settings = z.infer<typeof settingsSchema>

/**
 * Every field is optional on update; the server merges onto the current row.
 * For paymentFeeRates, use .partial() so a patch can update individual methods
 * without requiring all of them, and the service deep-merges with existing values.
 */
export const settingsUpdateSchema = settingsSchema.partial().extend({
  paymentFeeRates: z.record(
    z.enum(['cash', 'bank_transfer', 'card_machine', 'link_tabby', 'link_tamara', 'link_card', 'other']),
    z.object({
      rateBp: basisPointsSchema.optional(),
      fixedMinor: z.number().int().min(0).optional(),
    }),
  ).optional(),
})
export type SettingsUpdate = z.infer<typeof settingsUpdateSchema>

export const ACCOUNTING_MODES = ['simplified', 'advanced'] as const
export type AccountingMode = (typeof ACCOUNTING_MODES)[number]

export type WhatsappTemplateVars = {
  client_name: string
  business_name: string
  jobcard_number: string
  document_number: string
  document_type: string
  car: string
  total: string
  link: string
}

/**
 * Fill the WhatsApp message template. Unknown placeholders are left intact
 * rather than blanked, so a typo in the template is visible instead of silently
 * producing a message with a hole in it.
 */
export function renderWhatsappMessage(template: string, vars: WhatsappTemplateVars): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
    key in vars ? vars[key as keyof WhatsappTemplateVars] : match,
  )
}

/** Build a wa.me deep link. NOTE: this scheme cannot attach a file. */
export function waMeUrl(phoneDigits: string, message: string): string {
  return `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}`
}
